import React, { useEffect } from 'react';
import { ADMIN_PATH, PortalProvider, STAFF_PATH, STAFF_RESET_PATH, STAFF_SETUP_PATH, usePortal } from './contexts/PortalContext';
import { Login } from './pages/Login';
import { StaffLogin } from './pages/StaffLogin';
import { StaffSetupPassword } from './pages/StaffSetupPassword';
import { ClientApp } from './pages/ClientApp';
import { PortalApp } from './pages/PortalApp';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ViewToggle } from './components/ViewToggle';
import { Toast } from './components/Toast';
import { installAppIcons } from './utils/homeScreen';
import { CLIENT_ACCESS_TITLE, STAFF_TITLE, projectTitle, setPageTitle } from './utils/pageTitle';
function Portal() {
  const {
    path,
    navigate,
    staffAuthed,
    staffChecking,
    authed,
    activeProject,
    clientPreview,
    previewRequested
  } = usePortal();
  const onAdmin = path.startsWith(ADMIN_PATH);
  /* Checked first: an invitation link must never fall through to sign-in. */
  const onStaffSetup = path.startsWith(STAFF_SETUP_PATH) || path.startsWith(STAFF_RESET_PATH);
  const onStaffLogin = !onStaffSetup && path.startsWith(STAFF_PATH);

  /* Staff pages carry the Portal name; a client tab carries their own, taken
     from the stored client_name once the project has loaded. */
  useEffect(() => {
    const staffSide = onAdmin || onStaffLogin || onStaffSetup;
    if (staffSide) {
      setPageTitle(STAFF_TITLE);
      return;
    }
    if (!authed) {
      setPageTitle(CLIENT_ACCESS_TITLE);
      return;
    }
    setPageTitle(projectTitle(activeProject.client));
  }, [onAdmin, onStaffLogin, onStaffSetup, authed, activeProject.client]);

  // /admin is protected: without a verified Supabase staff session you are sent
  // to /staff, and knowing the URL is never enough. Signed in, /staff hands you
  // on to /admin. Both wait for the persisted session to be checked first.
  useEffect(() => {
    if (staffChecking) return;
    if (onAdmin && !staffAuthed) navigate(STAFF_PATH);
    if (onStaffLogin && staffAuthed) navigate(ADMIN_PATH);
  }, [onAdmin, onStaffLogin, staffAuthed, staffChecking, navigate]);

  /* An invitation link: the session it carries is read here, so no redirect
     and no session check runs first. */
  if (onStaffSetup) {
    return <>
        <StaffSetupPassword data-id="element-0" data-mp="86w6svjrae2f" />
        <Toast data-id="element-1" data-mp="83i3nl39q0tx" />
      </>;
  }
  if (onAdmin) {
    if (staffChecking) return <div className="min-h-screen w-full bg-ink" data-id="element-2" data-mp="3gm61vjp8aj0p" />;
    if (!staffAuthed) {
      return <>
          <StaffLogin data-id="element-3" data-mp="3bnuvjiy56qn8" />
          <Toast data-id="element-4" data-mp="3bkgqu420a5cm" />
        </>;
    }
    return <>
        <PortalApp data-id="element-5" data-mp="3qbzcoihjqu3w" />
        <ViewToggle data-id="element-6" data-mp="3qdaj6q2dmfyp" />
        <Toast data-id="element-7" data-mp="3lkhe212zx5m3" />
      </>;
  }
  if (onStaffLogin) {
    /* Nothing renders until the persisted session has been checked, so an
       already-signed-in staff member never sees the login form flash. */
    if (staffChecking) return <div className="min-h-screen w-full bg-ink" data-id="element-8" data-mp="2swliizp5xjqw" />;
    return <>
        <StaffLogin data-id="element-9" data-mp="18e1bbp26nwm5" />
        <ViewToggle data-id="element-10" data-mp="18dhphx9ewo2u" />
        <Toast data-id="element-11" data-mp="18czpcpjmpppn" />
      </>;
  }

  /* A preview link holds until the staff session has been verified, so the
     client name prompt never flashes on the way in. */
  if (previewRequested && staffChecking) {
    return <div className="min-h-screen w-full bg-paper" data-id="element-12" data-mp="yheowvu7kxi0" />;
  }

  /* Preview as Client is the client link itself, read by a staff session, so
     it skips the name prompt rather than taking a different route. */
  if (!authed && !clientPreview) {
    return <>
        <Login data-id="element-13" data-mp="1w2aq470zbk3s" />
        <ViewToggle data-id="element-14" data-mp="1w2txitt06q3k" />
      </>;
  }
  return <>
      <ClientApp data-id="element-15" data-mp="1i4417ge0cug9" />
      {/* Nothing is added around a preview: it must look exactly like what
          the client opens. */}
      {!clientPreview && <ViewToggle data-id="element-16" data-mp="1m3lakkwk2bqp" />}
      {!clientPreview && <Toast data-id="element-17" data-mp="6ofgyws529j5" />}
    </>;
}
export function App() {
  /**
   * The app icon, at boot (24 Sep 2026).
   *
   * It used to be installed from PortalApp, which only mounts once you are
   * signed in — and /admin sends a signed-out visitor to /staff, so a browser
   * adding the page to a Dock or a home screen never saw it. What it used
   * instead was the one icon the host page ships: a 32-pixel favicon,
   * enlarged. Four rebuilds of the artwork could not fix a tag that was never
   * read.
   *
   * Here it runs on every route, signed in or not. Only the picture: opening
   * without browser chrome is still staff-only, from PortalApp, because that
   * would take the back button away from a client.
   */
  useEffect(() => {
    void installAppIcons();
  }, []);
  return <PortalProvider data-id="element-18" data-mp="14v3up3swqkpz">
      <ErrorBoundary data-id="element-19" data-mp="zxk4r6mukji7">
        <Portal data-id="element-20" data-mp="zwsiwnj1y054" />
      </ErrorBoundary>
    </PortalProvider>;
}